import type {
  BudgetServiceLine,
  BudgetSubServiceLine,
  EffortLevel,
  Service,
} from "../types";
import { resolvePriceForCarEffort } from "./serviceEffortPrice";

export function parseBudgetPrice(value: number | string | undefined): number {
  if (value == null || value === "") return 0;
  const n = typeof value === "string" ? parseFloat(value) : value;
  return Number.isFinite(n) ? n : 0;
}

export function formatBudgetCurrency(value: number | string): string {
  const n = parseBudgetPrice(value);
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

export function getActiveSubServiceRelations(service: Service) {
  return (service.subServiceRelations ?? []).filter(
    (rel) => rel.isActive !== false && rel.subService?.isActive !== false
  );
}

export function getRequiredSubServiceRelations(service: Service) {
  return getActiveSubServiceRelations(service).filter((rel) => rel.isRequired);
}

export function getOptionalSubServiceRelations(service: Service) {
  return getActiveSubServiceRelations(service).filter((rel) => !rel.isRequired);
}

export function getServiceSuggestedPrice(
  service: Service,
  effort?: EffortLevel | string | null,
  optionalSubServiceIds: number[] = []
): number {
  const base = parseBudgetPrice(resolvePriceForCarEffort(service, effort));
  const relations = getActiveSubServiceRelations(service);

  const requiredTotal = relations
    .filter((rel) => rel.isRequired)
    .reduce((sum, rel) => sum + parseBudgetPrice(rel.price), 0);

  const optionalTotal = relations
    .filter(
      (rel) =>
        !rel.isRequired && optionalSubServiceIds.includes(rel.subServiceId)
    )
    .reduce((sum, rel) => sum + parseBudgetPrice(rel.price), 0);

  return Math.round((base + requiredTotal + optionalTotal) * 100) / 100;
}

type RawBudgetSubService = {
  id?: number;
  serviceSubServiceId?: number;
  subServiceId?: number;
  name?: string;
  description?: string;
  price?: number | string;
  isRequired?: boolean;
  serviceSubService?: {
    id?: number;
    subServiceId?: number;
    isRequired?: boolean;
    subService?: { id?: number; name?: string; description?: string };
  };
};

function flattenBudgetSubService(sub: RawBudgetSubService): BudgetSubServiceLine {
  const nested = sub.serviceSubService;

  return {
    id: sub.id,
    serviceSubServiceId: sub.serviceSubServiceId ?? nested?.id,
    subServiceId:
      sub.subServiceId ??
      nested?.subServiceId ??
      nested?.subService?.id ??
      0,
    name: sub.name ?? nested?.subService?.name ?? "Subserviço",
    description: sub.description ?? nested?.subService?.description,
    price: sub.price ?? 0,
    isRequired: sub.isRequired ?? nested?.isRequired ?? false,
  };
}

export function normalizeBudgetSubServices(
  line: BudgetServiceLine
): BudgetSubServiceLine[] {
  const rawSubs = line.subServices as RawBudgetSubService[] | undefined;
  if (!Array.isArray(rawSubs) || rawSubs.length === 0) return [];

  return rawSubs.map(flattenBudgetSubService);
}

/** Optional subservices selected on a budget line — sent to appointment API. */
export function getBudgetLineOptionalSubServiceIds(
  line: BudgetServiceLine
): number[] {
  return normalizeBudgetSubServices(line)
    .filter((sub) => !sub.isRequired && sub.subServiceId > 0)
    .map((sub) => sub.subServiceId);
}

export function getBudgetServices(budget: {
  services?: BudgetServiceLine[];
  items?: BudgetServiceLine[];
  budgetServices?: BudgetServiceLine[];
}): BudgetServiceLine[] {
  if (Array.isArray(budget.services) && budget.services.length > 0) {
    return budget.services;
  }

  if (Array.isArray(budget.items) && budget.items.length > 0) {
    return budget.items;
  }

  if (Array.isArray(budget.budgetServices)) {
    return budget.budgetServices;
  }

  return [];
}

export function computeBudgetTotal(
  services?: BudgetServiceLine[] | null,
  fallbackTotal?: number | string | null
): number {
  if (Array.isArray(services) && services.length > 0) {
    return services.reduce(
      (sum, line) => sum + parseBudgetPrice(line.price),
      0
    );
  }

  if (fallbackTotal != null) {
    return parseBudgetPrice(fallbackTotal);
  }

  return 0;
}

export function resolveBudgetTotal(budget: {
  services?: BudgetServiceLine[];
  items?: BudgetServiceLine[];
  total?: number | string;
}): number {
  return computeBudgetTotal(getBudgetServices(budget), budget.total);
}

export function computeBudgetTotalFromLines(
  lines: { price: number }[]
): number {
  if (!Array.isArray(lines)) return 0;
  return lines.reduce((sum, line) => sum + line.price, 0);
}

export function computeDiscountPercent(
  suggested: number,
  finalPrice: number
): number {
  if (suggested <= 0) return 0;
  const pct = ((suggested - finalPrice) / suggested) * 100;
  return Math.max(0, Math.min(100, Math.round(pct * 100) / 100));
}

export function applyDiscountPercent(
  suggested: number,
  discountPercent: number
): number {
  const pct = Math.max(0, Math.min(100, discountPercent));
  const price = suggested * (1 - pct / 100);
  return Math.round(price * 100) / 100;
}

export function getBudgetStatusLabel(
  status: "Created" | "Completed" | "Cancelled"
): string {
  switch (status) {
    case "Created":
      return "Aberto";
    case "Completed":
      return "Concluído";
    case "Cancelled":
      return "Cancelado";
    default:
      return status;
  }
}

export function getBudgetStatusClasses(
  status: "Created" | "Completed" | "Cancelled"
): string {
  switch (status) {
    case "Created":
      return "bg-sky-50 text-sky-700 border-sky-200";
    case "Completed":
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "Cancelled":
      return "bg-gray-100 text-gray-600 border-gray-200";
    default:
      return "bg-gray-100 text-gray-600 border-gray-200";
  }
}

export function getBudgetLineLabel(line: BudgetServiceLine): string {
  return line.service?.name ?? `Serviço #${line.serviceId}`;
}

export function getBudgetLineSubServiceLabels(line: BudgetServiceLine): string[] {
  return normalizeBudgetSubServices(line).map((sub) => sub.name);
}
